"use client";
import { useCallback, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { StripeSession } from "../types";
import useStripeState from "./useStripeState";

interface Props {
  enabled: boolean | null;
  loadSession: () => Promise<StripeSession>;
}

export function useInitializeStripe({ enabled, loadSession }: Props) {
  const {
    markInitializing,
    markError,
    markReady,
    markSuccess,
    resetState,
    state,
  } = useStripeState();

  const handleInitialize = useCallback(async () => {
    markInitializing();
    try {
      const session = await loadSession();
      if (!session.clientSecret || !session.publishableKey) {
        markError({
          message: "Could not initialize payment. Please try again.",
          retryType: "backend",
        });
        return;
      }
      const stripePromise = loadStripe(session.publishableKey, {
        stripeAccount: session.connectedAccountId ?? undefined,
      });
      markReady({
        stripePromise,
        clientSecret: session.clientSecret,
      });
    } catch {
      markError({
        message: "Could not reach the server. Please try again.",
        retryType: "backend",
      });
    }
  }, [loadSession, markError, markInitializing, markReady]);

  useEffect(() => {
    if (enabled) {
      handleInitialize();
    }
  }, [handleInitialize, enabled]);

  return {
    state,
    resetState,
    markSuccess,
    handleInitialize,
  };
}
